import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import "./PatientRegistrationWizard.scss";
import MyAppointments from "./MyAppointments";
import {
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  Grid,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import NavLogoFooter from "../../assets/images/logo/NavLogo_footer_white.svg";
import NavLogoDefault from "../../assets/images/logo/B-T-HTMATE.svg";
import HTLifeLogo from "../../assets/images/logo/B-T-HTLIFE.svg";
import VoiceAssistant from "../../components/VoiceAssistant/VoiceAssistant";


const steps = ["Phone Verification", "Patient Details"];

const formatTimer = (seconds) => {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

const decodeEmbeddedParams = (encodedValue) => {
  try {
    if (!encodedValue) return null;
    const base64String = atob(decodeURIComponent(encodedValue));
    const byteArray = Uint8Array.from(base64String, (char) =>
      char.charCodeAt(0)
    );
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(byteArray);
    return JSON.parse(jsonString);
  } catch (err) {
    console.error("Failed to decode registration QR payload", err);
    return null;
  }
};

const buildRegistrationHeaders = (
  appMode,
  orgCode,
  facilityName,
  authToken
) => {
  const headers = {
    "Content-Type": "application/json",
  };

  if (appMode) {
    headers["APP-MODE"] = appMode;
  }
  if (orgCode) {
    headers["ORG-CODE"] = orgCode;
  }
  if (facilityName) {
    headers["FACILITY"] = facilityName;
  }
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  return headers;
};

const inferImageMimeFromBase64 = (base64 = "") => {
  const trimmed = (base64 || "").trim();
  // Common base64 signatures:
  // - JPEG: /9j
  // - PNG: iVBOR
  // - GIF: R0lGOD
  if (trimmed.startsWith("/9j")) return "image/jpeg";
  if (trimmed.startsWith("iVBOR")) return "image/png";
  if (trimmed.startsWith("R0lGOD")) return "image/gif";
  return "image/png";
};

const QR_FLOW_STORAGE_KEY = "patient_qr_registration_flow";
const FLOW_ROUTE_BY_VIEW = {
  login: "/patient_login",
  voice: "/book_appointment",
  appointments: "/my_appointment",
  dashboard: "/patient_dashboard",
};
const FLOW_VIEW_BY_ROUTE = {
  "/patient_login": "login",
  "/book_appointment": "voice",
  "/my_appointment": "appointments",
  "/patient_dashboard": "dashboard",
};

const getFlowViewFromSearch = (search = "") =>
  new URLSearchParams(search).get("view") || "";

const buildFlowRoute = (view, pathname, search = "") => {
  const params = new URLSearchParams(search);
  if (view && view !== "register_phone") {
    params.set("view", view);
  } else {
    params.delete("view");
  }
  const query = params.toString();

  let targetPath =
    FLOW_ROUTE_BY_VIEW[view] ||
    (pathname === "/register_patient_qr" ? pathname : "/register_patient_qr");

  return {
    path: query ? `${targetPath}?${query}` : targetPath,
    view: view || "register_phone",
  };
};

const resolveFlowView = (pathname, search, saved, isPatientLoginRoute) => {
  const routeView = FLOW_VIEW_BY_ROUTE[pathname] || "";
  let view = getFlowViewFromSearch(search) || routeView || saved?.view || "";

  if (!view && isPatientLoginRoute) {
    view = "login";
  }

  if (
    pathname === "/register_patient_qr" &&
    saved?.registeredPatientId &&
    (view === "register_phone" || view === "register_details")
  ) {
    view = "login";
  }

  return view;
};

const saveQrFlowToSession = (payload) => {
  try {
    sessionStorage.setItem(QR_FLOW_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn("Could not persist QR registration flow", error);
  }
};

const loadQrFlowFromSession = () => {
  try {
    const raw = sessionStorage.getItem(QR_FLOW_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const clearQrFlowSession = () => {
  sessionStorage.removeItem(QR_FLOW_STORAGE_KEY);
};

function PatientRegistrationWizard({ initialView } = {}) {
  const history = useHistory();
  const location = useLocation();
  const isPatientLoginRoute =
    location.pathname === "/patient_login" || initialView === "login";
  const [activeStep, setActiveStep] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState(["", "", "", "", "", ""]);
  const [authToken, setAuthToken] = useState("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [otpTimer, setOtpTimer] = useState(60);
  const [orgCode, setOrgCode] = useState("");
  const [appMode, setAppMode] = useState("");
  const [facilityName, setFacilityName] = useState("");
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState("");
  const [isMobileOtpVerified, setIsMobileOtpVerified] = useState(false);
  const [navLogoSrc, setNavLogoSrc] = useState(null);
  const [showVoiceAssistant, setShowVoiceAssistant] = useState(false);
  const [voiceShowResults, setVoiceShowResults] = useState(false);
  const [registeredPatientId, setRegisteredPatientId] = useState(null);
  const [patientUsername, setPatientUsername] = useState("");
  const [patientPassword, setPatientPassword] = useState("");

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successCountdown, setSuccessCountdown] = useState(5);
  const [showLogin, setShowLogin] = useState(false);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginNeedsVerification, setLoginNeedsVerification] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [isReVerifyingForLogin, setIsReVerifyingForLogin] = useState(false);

  const [showDashboard, setShowDashboard] = useState(false);
  const [showMyAppointments, setShowMyAppointments] = useState(false);
  const [htlifeAccessToken, setHtlifeAccessToken] = useState("");
  const [htlifeRefreshToken, setHtlifeRefreshToken] = useState("");
  const [patientProfile, setPatientProfile] = useState(null);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const [registeredPatientName, setRegisteredPatientName] = useState("");

  const today = new Date().toISOString().split("T")[0];

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      country: "India",
      gender: "",
      state: "",
      district: "",
      insuranceScheme: [],
      patientName: "",
      username: "",
      password: "",
    },
  });

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const REACT_APP_BACKEND_ROUTE_URL_HTLIFE =
    process.env.REACT_APP_BACKEND_ROUTE_URL_HTLIFE;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompactOtp = useMediaQuery("(max-width:430px)");
  const isOtpDisabled = !otpSent;
  const headerStyle = {
    fontSize: isMobile ? "20px" : "28px",
    fontWeight: 600,
    color: "#0d1b3d",
  };
  const primaryControlHeight = isMobile ? "46px" : "52px";
  const watchedPhoneNumber = watch("phoneNumber");
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const isRestoringFlowRef = useRef(true);
  const skipNextAutoPersistRef = useRef(false);
  const isInternalNavigationRef = useRef(false);
  const hasMountedRef = useRef(false);

  const navigateFlowView = useCallback(
    (view, usePush = false) => {
      const { path, view: routeView } = buildFlowRoute(
        view,
        location.pathname,
        location.search
      );
      const state = { view: routeView };
      isInternalNavigationRef.current = true;
      if (usePush) {
        history.push(path, state);
      } else {
        history.replace(path, state);
      }
    },
    [history, location.pathname, location.search]
  );

  const applyFlowView = useCallback((view, saved = {}) => {
    setShowSuccessModal(false);
    setShowVoiceAssistant(false);
    setShowMyAppointments(false);
    setShowDashboard(false);
    setShowLogin(false);

    if (saved.registeredPatientId != null) {
      setRegisteredPatientId(saved.registeredPatientId);
    }
    if (saved.patientUsername) setPatientUsername(saved.patientUsername);
    if (saved.patientPassword) setPatientPassword(saved.patientPassword);
    if (saved.registeredPatientName) {
      setRegisteredPatientName(saved.registeredPatientName);
    }
    if (saved.verifiedPhoneNumber) {
      setVerifiedPhoneNumber(saved.verifiedPhoneNumber);
      setValue("phoneNumberConfirm", saved.verifiedPhoneNumber, {
        shouldValidate: false,
      });
    }
    if (saved.isMobileOtpVerified) setIsMobileOtpVerified(true);

    const storedHtlifeAccess = sessionStorage.getItem("htlifeAccessToken");
    const storedHtlifeRefresh = sessionStorage.getItem("htlifeRefreshToken");
    if (storedHtlifeAccess) setHtlifeAccessToken(storedHtlifeAccess);
    if (storedHtlifeRefresh) setHtlifeRefreshToken(storedHtlifeRefresh);
    if (saved.patientProfile) setPatientProfile(saved.patientProfile);

    switch (view) {
      case "register_phone":
        setActiveStep(0);
        // Always unlock step 1 phone input when returning here.
        setOtpSent(false);
        setOtpValue(["", "", "", "", "", ""]);
        setOtpError(false);
        setIsVerifyingOtp(false);
        setOtpTimer(60);
        setIsMobileOtpVerified(false);
        break;
      case "voice":
        setShowVoiceAssistant(true);
        setActiveStep(1);
        break;
      case "appointments":
        setShowMyAppointments(true);
        setActiveStep(1);
        break;
      case "dashboard":
        setShowDashboard(true);
        setActiveStep(1);
        break;
      case "login":
        setShowLogin(true);
        setActiveStep(1);
        setIsMobileOtpVerified(true);
        break;
      case "register_details":
        setActiveStep(1);
        setIsMobileOtpVerified(true);
        break;
      default:
        setActiveStep(0);
        // Defensive reset for any unknown/empty view that lands on step 1.
        setOtpSent(false);
        setOtpValue(["", "", "", "", "", ""]);
        setOtpError(false);
        setIsVerifyingOtp(false);
        setOtpTimer(60);
        break;
    }
  }, [setValue]);

  const buildFlowSnapshot = useCallback(
    (viewOverride) => {
      let view = viewOverride;
      if (!view) {
        if (showVoiceAssistant) view = "voice";
        else if (showMyAppointments) view = "appointments";
        else if (showDashboard) view = "dashboard";
        else if (showLogin || showSuccessModal) view = "login";
        else if (activeStep >= 1) view = "register_details";
        else view = "register_phone";
      }
      return {
        view,
        activeStep,
        isMobileOtpVerified,
        verifiedPhoneNumber,
        registeredPatientId,
        patientUsername,
        patientPassword,
        registeredPatientName,
        patientProfile,
      };
    },
    [
      showVoiceAssistant,
      showMyAppointments,
      showDashboard,
      showLogin,
      showSuccessModal,
      activeStep,
      isMobileOtpVerified,
      verifiedPhoneNumber,
      registeredPatientId,
      patientUsername,
      patientPassword,
      registeredPatientName,
      patientProfile,
    ]
  );

  const persistFlowState = useCallback(
    (viewOverride, usePush = false, extra = {}) => {
      skipNextAutoPersistRef.current = true;
      const snapshot = { ...buildFlowSnapshot(viewOverride), ...extra };
      saveQrFlowToSession(snapshot);
      navigateFlowView(snapshot.view, usePush);
    },
    [buildFlowSnapshot, navigateFlowView]
  );

  // Intentionally run once on mount: reads URL params and fetches facility branding for this session.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const token = sessionStorage.getItem("accessToken");
    setAuthToken(token);

    // Extract query parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const decodedPayload = decodeEmbeddedParams(urlParams.get("data"));

    const orgCodeParam = decodedPayload?.org_code || urlParams.get("org_code");
    const appModeParam = decodedPayload?.app_mode || urlParams.get("app_mode");
    const facilityParam =
      decodedPayload?.facility || urlParams.get("facility");
    if (orgCodeParam) {
      setOrgCode(orgCodeParam);
      sessionStorage.setItem("ORG_CODE", orgCodeParam);
    }
    if (appModeParam) {
      setAppMode(appModeParam);
      sessionStorage.setItem("APP_MODE", appModeParam);
    }
    if (facilityParam) {
      const normalizedFacility = facilityParam.replace(/\+/g, " ");
      setFacilityName(normalizedFacility);
    }

    if (orgCodeParam && appModeParam) {
      fetchFacilityBrandConfiguration(appModeParam, orgCodeParam);
    }

    const saved = loadQrFlowFromSession();
    const viewToRestore = resolveFlowView(
      location.pathname,
      location.search,
      saved,
      isPatientLoginRoute
    );

    if (viewToRestore) {
      isRestoringFlowRef.current = true;
      applyFlowView(viewToRestore, saved || {});
      const { path } = buildFlowRoute(
        viewToRestore,
        location.pathname,
        location.search
      );
      const currentPath = `${location.pathname}${location.search}`;
      if (currentPath !== path) {
        isInternalNavigationRef.current = true;
        history.replace(path, { view: viewToRestore });
      }
    }

    hasMountedRef.current = true;
  }, []);

  useEffect(() => {
    if (isRestoringFlowRef.current) {
      isRestoringFlowRef.current = false;
      return;
    }
    if (skipNextAutoPersistRef.current) {
      skipNextAutoPersistRef.current = false;
      return;
    }
    persistFlowState();
  }, [
    activeStep,
    showLogin,
    showDashboard,
    showMyAppointments,
    showVoiceAssistant,
    showSuccessModal,
    isMobileOtpVerified,
    verifiedPhoneNumber,
    registeredPatientId,
    patientUsername,
    patientPassword,
    registeredPatientName,
    patientProfile,
    persistFlowState,
  ]);

  useEffect(() => {
    const handleSessionExpired = () => {
      toast.error("Your session has expired. Please log in again.");
      resetPatientSession();
    };
    window.addEventListener("htlife-session-expired", handleSessionExpired);
    return () => window.removeEventListener("htlife-session-expired", handleSessionExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasMountedRef.current) return;
    if (isInternalNavigationRef.current) {
      isInternalNavigationRef.current = false;
      return;
    }

    const saved = loadQrFlowFromSession();
    const view = resolveFlowView(
      location.pathname,
      location.search,
      saved,
      location.pathname === "/patient_login"
    );

    if (!view) return;

    isRestoringFlowRef.current = true;
    applyFlowView(view, saved || {});
  }, [location.pathname, location.search, applyFlowView]);


  const fetchFacilityBrandConfiguration = async (appModeVal, orgCodeVal) => {
    try {
      console.log("orgCodeParam", orgCodeVal);
      console.log("appModeParam", appModeVal);
      if (orgCodeVal && appModeVal) {
        console.log("fetching facility brand configuration");
        const response = await axios.get(
          `${REACT_APP_BACKEND_ROUTE_URL}get_facility_brand_configuration/`,
          {
            headers: {
              "APP-MODE": appModeVal,
              "ORG-CODE": orgCodeVal,
            },
          }
        );

        if (
          response?.data?.status === "Success" &&
          Array.isArray(response?.data?.data) &&
          response?.data?.data?.[0]?.content
        ) {
          const base64 = String(response.data.data[0].content || "").trim();
          const mime = inferImageMimeFromBase64(base64);
          setNavLogoSrc(`data:${mime};base64,${base64}`);
        } else {
          setNavLogoSrc(NavLogoDefault);
        }
      } else {
        setNavLogoSrc(NavLogoDefault);
      }
    } catch (error) {
      // Non-blocking: patient registration should still work without branding.
      console.error("Error fetching facility brand configuration:", error);
      setNavLogoSrc(NavLogoDefault);
    }
  };

  useEffect(() => {
    if (watchedPhoneNumber !== undefined) {
      setValue("phoneNumberConfirm", watchedPhoneNumber);
    }
  }, [watchedPhoneNumber, setValue]);

  useEffect(() => {
    if (activeStep === 1) {
      const phoneToPersist = verifiedPhoneNumber || watchedPhoneNumber;
      if (phoneToPersist) {
        setValue("phoneNumberConfirm", phoneToPersist, {
          shouldValidate: true,
        });
      }
    }
  }, [activeStep, verifiedPhoneNumber, watchedPhoneNumber, setValue]);

  useEffect(() => {
    if (!otpSent || activeStep !== 0 || otpTimer <= 0) return;

    const timerId = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [otpSent, activeStep, otpTimer]);


  useEffect(() => {
    if (!showSuccessModal) return;
    if (successCountdown <= 0) {
      setShowSuccessModal(false);
      if (patientUsername && patientPassword && REACT_APP_BACKEND_ROUTE_URL_HTLIFE) {
        handlePatientLogin(null, patientUsername, patientPassword);
      } else {
        setShowLogin(true);
        persistFlowState("login", true);
      }
      return;
    }
    const id = setTimeout(() => setSuccessCountdown((prev) => prev - 1), 1000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuccessModal, successCountdown]);

  useEffect(() => {
    if (otpSent && activeStep === 0 && otpTimer === 0) {
      setOtpSent(false);
      setOtpValue(["", "", "", "", "", ""]);
      setOtpError(false);
      setOtpTimer(60);
    }
  }, [otpTimer, otpSent, activeStep]);

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const phoneNumber = getValues("phoneNumber");
    if (!phoneNumber) {
      toast.error("Please enter phone number");
      return;
    }

    try {
      const payload = {
        mobile_no: phoneNumber,
      };

      if (!REACT_APP_BACKEND_ROUTE_URL) {
        toast.error("Backend URL is not configured. Please contact support.");
        return;
      }

      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}register_patient_mobile_no/`,
        JSON.stringify(payload),
        {
          headers: buildRegistrationHeaders(
            appMode,
            orgCode,
          ),
        }
      );

      if (response?.data?.status === "Success") {
        toast.success("OTP sent successfully!");
        setOtpSent(true);
        setOtpError(false);
        setOtpTimer(60);
        setOtpValue(["", "", "", "", "", ""]);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error(
        error?.response?.data?.message ||
        "Unable to send OTP. Please try again."
      );
    }
  };

  const verifyOtp = async (otpCode) => {
    if (!otpSent || otpCode.length !== 6) return;

    const phoneNumber = getValues("phoneNumber");
    if (!phoneNumber) {
      toast.error("Phone number is required before verifying OTP.");
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError(false);

    try {
      const payload = {
        mobile_no: phoneNumber,
        otp: otpCode,
      };

      if (!REACT_APP_BACKEND_ROUTE_URL) {
        toast.error("Backend URL is not configured. Please contact support.");
        setIsVerifyingOtp(false);
        return;
      }

      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}verify_patient_mobile_no/`,
        JSON.stringify(payload),
        {
          headers: buildRegistrationHeaders(
            appMode,
            orgCode,
          ),
        }
      );

      const statusText = response?.data?.status?.toLowerCase();
      const isVerified =
        statusText === "success" && response?.data?.is_valid === true;

      if (isVerified) {
        toast.success("OTP verified successfully!");
        setIsMobileOtpVerified(true);
        setActiveStep(1);
        setOtpTimer(0);
        setVerifiedPhoneNumber(phoneNumber);
        setValue("phoneNumberConfirm", phoneNumber, { shouldValidate: true });
        if (isReVerifyingForLogin && patientUsername && patientPassword) {
          setIsReVerifyingForLogin(false);
          handlePatientLogin(null, patientUsername, patientPassword);
        } else {
          setTimeout(() => persistFlowState("register_details", true), 0);
        }
      } else {
        setOtpError(true);
        toast.error(
          response?.data?.message || "Invalid OTP. Please try again."
        );
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setOtpError(true);
      toast.error(
        error?.response?.data?.message ||
        "Unable to verify OTP. Please try again."
      );
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (isVerifyingOtp) return;

    const digit = value.replace(/\D/g, "");
    if (digit.length > 1) return; // Only allow single digit

    const newOtp = [...otpValue];
    newOtp[index] = digit;
    setOtpValue(newOtp);
    setOtpError(false);

    // Auto-focus next input
    if (digit && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    const enteredOtp = newOtp.join("");
    const isOtpComplete = newOtp.every((char) => char !== "");
    if (isOtpComplete && enteredOtp.length === 6) {
      verifyOtp(enteredOtp);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValue[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const onSubmitPatient = async (data) => {
    console.log("Patient data:", data);

    try {
      const dateParts = data.dateOfBirth.split("-");
      const facilityDisplayName =
        facilityName || sessionStorage.getItem("facility_display_name") || "";

      const resolvedPhone =
        data.phoneNumberConfirm || verifiedPhoneNumber || watchedPhoneNumber;
      const resolvedEmail =
        (data.emailId || "").trim() ||
        `${String(resolvedPhone).replace(/\D/g, "")}@qr.hlthtek.local`;

      const formattedData = {
        facilityName: facilityDisplayName,
        patientName: data.patientName,
        gender: data.gender,
        locality: data.locality,
        address: data.locality, // Using locality as address if address field is not separate
        city: data.city || "",
        pincode: data.pincode,
        phoneNumber: resolvedPhone,
        emailId: resolvedEmail,
        yearOfBirth: dateParts[0],
        monthOfBirth: dateParts[1],
        dayOfBirth: dateParts[2],
        username: data.username,
        password: data.password,
        // Tell backend this patient completed QR OTP verification before register.
        mobile_verified: isMobileOtpVerified,
        email_verified: isMobileOtpVerified,
        otp_verified: isMobileOtpVerified,
        verified_mobile_no: verifiedPhoneNumber || resolvedPhone,
        registration_source: "qr",
      };

      console.log("Formatted patient data for submission:", formattedData);

      if (!REACT_APP_BACKEND_ROUTE_URL) {
        toast.error("Backend URL is not configured. Please contact support.");
        return;
      }

      const headers = {
        ...buildRegistrationHeaders(
          appMode,
          orgCode,
          facilityDisplayName,
        ),
      };

      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}register_patient/`,
        JSON.stringify(formattedData),
        {
          headers,
        }
      );

      // if (response?.data?.message === "Patient created successfully !!") {
      //   const patientId = response?.data?.patient?.patient_id;
      if (response?.data?.status === "Success" || response?.data?.message === "Patient created successfully !!") {
        const patientId = response?.data?.data?.patient_id;

        setRegisteredPatientId(patientId);
        setPatientUsername(data?.username);
        setPatientPassword(data?.password);
        setRegisteredPatientName(data?.patientName || "");

        setLoginUsername(data?.username || "");
        setLoginPassword(data?.password || "");
        setLoginError("");
        setShowLogin(false);
        setShowVoiceAssistant(false);
        setSuccessCountdown(5);
        setShowSuccessModal(true);
        persistFlowState("login", true, {
          registeredPatientId: patientId,
          patientUsername: data?.username || "",
          patientPassword: data?.password || "",
          registeredPatientName: data?.patientName || "",
          isMobileOtpVerified: true,
          verifiedPhoneNumber: verifiedPhoneNumber || resolvedPhone,
          activeStep: 1,
        });
      } else {
        toast.error(
          response?.data?.message || response?.data?.error ||
          "Failed to register patient. Please try again."
        );
      }
    } catch (error) {
      console.error("Error registering patient:", error);
      const status = error?.response?.status;
      const isGatewayError =
        status === 502 || status === 503 || status === 504;
      const isTimeout =
        error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "");

      if (isGatewayError || isTimeout) {
        // 502/503/504 (or a timeout) means the request reached the server but the
        // registration service crashed or timed out. This is a backend/gateway issue,
        // not bad input — surface a clear, actionable message instead of a generic one.
        toast.error(
          "The registration server is temporarily unavailable (gateway error). Your details were not saved. Please wait a moment and try submitting again."
        );
      } else {
        toast.error(
          error?.response?.data?.message ||
          "Unable to register patient. Please try again."
        );
      }
    }
  };

  const parseHtlifeLoginResponse = (body = {}) => {
    const access =
      body.access || body.data?.access || body.data?.access_token || "";
    const refresh =
      body.refresh || body.data?.refresh || body.data?.refresh_token || "";
    const profileData = body.data;
    const profile =
      profileData &&
        typeof profileData === "object" &&
        !profileData.access &&
        !profileData.access_token
        ? profileData
        : null;

    return {
      access,
      refresh,
      profile,
      status: String(body.status || "").toLowerCase(),
      message: body.message || "",
    };
  };

  const isHtlifeLoginSuccess = (body) =>
    Boolean(parseHtlifeLoginResponse(body).access);

  const applyHtlifeLoginSuccess = (body, enteredUsername, password) => {
    const { access, refresh, profile } = parseHtlifeLoginResponse(body);
    if (!access) return;

    setHtlifeAccessToken(access);
    setHtlifeRefreshToken(refresh || "");
    setPatientProfile(profile || null);
    if (profile?.name) setRegisteredPatientName(profile.name);
    sessionStorage.setItem("htlifeAccessToken", access);
    if (refresh) {
      sessionStorage.setItem("htlifeRefreshToken", refresh);
    }
    setPatientUsername(enteredUsername);
    setPatientPassword(password);
    setShowLogin(false);
    setShowMyAppointments(false);
    setShowVoiceAssistant(false);
    setShowDashboard(true);
    setLoginError("");
    persistFlowState("dashboard", true);
  };

  const performHtlifeLogin = async (username, password) => {
    const response = await axios.post(
      `${REACT_APP_BACKEND_ROUTE_URL_HTLIFE}login/`,
      JSON.stringify({ username, password }),
      { headers: buildHtlifeHeaders() }
    );
    return response?.data || {};
  };

  const buildHtlifeHeaders = (accessToken = "") => {
    const headers = { "Content-Type": "application/json" };
    if (orgCode) headers["Org-Code"] = orgCode;
    if (appMode) headers["App-Mode"] = appMode;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return headers;
  };

  const resetPatientSession = () => {
    const saved = loadQrFlowFromSession();
    setHtlifeAccessToken("");
    setHtlifeRefreshToken("");
    setPatientProfile(null);
    sessionStorage.removeItem("htlifeAccessToken");
    sessionStorage.removeItem("htlifeRefreshToken");
    setShowDashboard(false);
    setShowMyAppointments(false);
    setShowVoiceAssistant(false);
    setShowLogin(true);
    setLoginUsername("");
    setLoginPassword("");
    setLoginError("");
    setActiveStep(1);
    setIsMobileOtpVerified(true);

    saveQrFlowToSession({
      view: "login",
      activeStep: 1,
      isMobileOtpVerified: true,
      verifiedPhoneNumber:
        saved?.verifiedPhoneNumber || verifiedPhoneNumber || "",
      registeredPatientId:
        saved?.registeredPatientId || registeredPatientId || null,
      patientUsername: saved?.patientUsername || patientUsername || "",
      patientPassword: saved?.patientPassword || patientPassword || "",
      registeredPatientName:
        saved?.registeredPatientName || registeredPatientName || "",
      patientProfile: null,
    });
    skipNextAutoPersistRef.current = true;
    navigateFlowView("login");
  };

  const isPatientLoggedIn =
    Boolean(htlifeAccessToken) &&
    (showDashboard || showMyAppointments || showVoiceAssistant);

  const handlePatientLogin = async (e, autoUsername, autoPassword) => {
    e?.preventDefault();
    const enteredUsername = (autoUsername || loginUsername).trim();
    const enteredPassword = autoPassword || loginPassword;
    if (!enteredUsername || !enteredPassword) {
      setLoginError("Please enter both username and password.");
      return;
    }

    if (!REACT_APP_BACKEND_ROUTE_URL_HTLIFE) {
      setLoginError("HTLife service URL is not configured. Please contact support.");
      return;
    }

    setIsLoginLoading(true);
    setLoginError("");
    setLoginNeedsVerification(false);

    try {
      const body = await performHtlifeLogin(enteredUsername, enteredPassword);
      if (isHtlifeLoginSuccess(body)) {
        applyHtlifeLoginSuccess(body, enteredUsername, enteredPassword);
        toast.success("Logged in successfully.");
      } else {
        setShowLogin(true);
        persistFlowState("login", true);
        const { message } = parseHtlifeLoginResponse(body);
        const unverified =
          body?.data?.mobile_verified === false ||
          body?.data?.email_verified === false;
        if (unverified) {
          setLoginNeedsVerification(true);
          setLoginError("Your account is not verified. Please verify your mobile number to continue.");
        } else {
          setLoginError(
            message || "Login failed. Please check your username and password."
          );
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      setShowLogin(true);
      persistFlowState("login", true);
      const errBody = error?.response?.data;
      const unverified =
        errBody?.data?.mobile_verified === false ||
        errBody?.data?.email_verified === false;
      if (unverified) {
        setLoginNeedsVerification(true);
        setLoginError("Your account is not verified. Please verify your mobile number to continue.");
      } else {
        setLoginError(
          errBody?.message ||
          "Unable to log in. Please check your username and password."
        );
      }
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handlePatientLogout = async () => {
    setIsLogoutLoading(true);
    try {
      if (htlifeAccessToken && REACT_APP_BACKEND_ROUTE_URL_HTLIFE) {
        const response = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL_HTLIFE}logout/`,
          JSON.stringify(
            htlifeRefreshToken ? { refresh: htlifeRefreshToken } : {}
          ),
          { headers: buildHtlifeHeaders(htlifeAccessToken) }
        );

        if (String(response?.data?.status || "").toLowerCase() === "success") {
          toast.success(response?.data?.message || "Logged out successfully.");
          sessionStorage.clear();
        }
      }
    } catch (error) {
      console.error("HTLife logout error:", error);
    } finally {
      resetPatientSession();
      setIsLogoutLoading(false);
    }
  };

  const renderErrorMessage = (error) =>
    error ? (
      <p className="errorMsg" style={{ color: "red", marginTop: "6px" }}>
        {error.message}
      </p>
    ) : null;

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        const otpBoxSize = isCompactOtp ? 44 : isMobile ? 48 : 60;
        const otpFontSize = isCompactOtp ? "18px" : isMobile ? "20px" : "24px";
        const otpGap = isCompactOtp ? "3px" : "12px";
        const otpInputStyle = {
          width: `${otpBoxSize}px`,
          height: `${otpBoxSize}px`,
          textAlign: "center",
          fontSize: otpFontSize,
          fontWeight: "bold",
          color: "#0d1b3d",
          caretColor: "#0d1b3d",
          WebkitTextFillColor: "#0d1b3d",
          lineHeight: `${otpBoxSize - 4}px`,
          padding: "0",
          fontFamily: "'Inter', 'Roboto Mono', monospace",
          border: `2px solid ${otpError ? "#d32f2f" : isOtpDisabled ? "#bdbdbd" : "#1976d2"
            }`,
          borderRadius: "8px",
          backgroundColor: otpError
            ? "#ffebee"
            : isOtpDisabled
              ? "#f5f5f5"
              : "#fff",
          transition: "border-color 0.2s",
          outline: "none",
          boxSizing: "border-box",
        };
        return (
          <div className="form-section mt-3">
            {/* Header with phone icon */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 22 }}>📞</span>
              <h4 style={{ ...headerStyle, margin: 0 }}>Phone Verification</h4>
            </div>
            <p style={{ color: "#6b7a99", fontSize: 14, marginBottom: 22, textAlign: "left" }}>
              We'll send a 6-digit code to confirm your number.
            </p>

            {/* Phone number + Send OTP row */}
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label
                htmlFor="phoneNumber"
                className="form-label"
                style={{ display: "flex", alignItems: "center", fontWeight: 600, marginBottom: 8 }}
              >
                Phone Number <span style={{ color: "red", marginLeft: 3 }}>*</span>
              </label>
              <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
                {/* Input with +91 prefix */}
                <div style={{
                  display: "flex",
                  border: `1.5px solid ${errors.phoneNumber ? "#d32f2f" : "#cbd5e1"}`,
                  borderRadius: 10,
                  overflow: "hidden",
                  flex: 1,
                  height: primaryControlHeight,
                  backgroundColor: otpSent ? "#f5f5f5" : "#fff",
                  transition: "border-color 0.2s",
                }}>
                  <div style={{
                    padding: "0 14px",
                    backgroundColor: "#eef2f7",
                    borderRight: "1.5px solid #cbd5e1",
                    fontWeight: 700,
                    color: "#0d1b3d",
                    fontSize: 15,
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    userSelect: "none",
                  }}>
                    +91
                  </div>
                  <input
                    id="phoneNumber"
                    type="tel"
                    placeholder="Enter 10-digit phone number"
                    maxLength={10}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, "");
                    }}
                    style={{
                      height: "100%",
                      border: "none",
                      outline: "none",
                      flex: 1,
                      padding: "0 12px",
                      fontSize: 15,
                      boxShadow: "none",
                      backgroundColor: "transparent",
                      width: 0,
                    }}
                    {...register("phoneNumber", {
                      required: "Phone Number is required.",
                      minLength: {
                        value: 10,
                        message: "Phone Number must be exactly 10 digits.",
                      },
                      maxLength: {
                        value: 10,
                        message: "Phone Number must be exactly 10 digits.",
                      },
                      pattern: {
                        value: /^[6-9][0-9]{9}$/,
                        message:
                          "Phone Number must start with 6, 7, 8, or 9 and be 10 digits.",
                      },
                      validate: {
                        onlyNumbers: (value) => {
                          if (!value) return true;
                          return (
                            /^[0-9]+$/.test(value) ||
                            "Phone Number can only contain numbers."
                          );
                        },
                        validLength: (value) => {
                          if (!value) return true;
                          return (
                            value.length === 10 ||
                            "Phone Number must be exactly 10 digits."
                          );
                        },
                      },
                    })}
                  />
                </div>

                {/* Send OTP — smaller, fixed width */}
                <button
                  type="button"
                  className="btn btn__secondary submit-details-btn"
                  onClick={handleSendOtp}
                  disabled={isVerifyingOtp}
                  style={{
                    height: primaryControlHeight,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    paddingLeft: 22,
                    paddingRight: 22,
                    borderRadius: 10,
                  }}
                >
                  {otpSent ? "OTP Sent" : "Send OTP →"}
                </button>
              </div>
              {renderErrorMessage(errors.phoneNumber)}
            </div>

            <div className="otp-section mt-4 text-center">
              <p className="fw-semibold mb-1">Enter 6-digit OTP</p>
              {isOtpDisabled && (
                <p className="text-muted mb-1">
                  Send OTP to enable this section.
                </p>
              )}
              {otpSent && otpTimer > 0 && (
                <p className="text-muted mb-1">
                  Time remaining: {formatTimer(otpTimer)}
                </p>
              )}
              {isVerifyingOtp && (
                <p className="text-muted mb-1">Verifying OTP...</p>
              )}
              {otpError && (
                <p style={{ color: "#d32f2f" }}>
                  Invalid OTP. Please try again.
                </p>
              )}

              <div
                className="d-flex justify-content-center flex-wrap mt-3"
                style={{
                  opacity: isOtpDisabled ? 0.7 : 1,
                  columnGap: otpGap,
                  rowGap: otpGap,
                }}
              >
                {otpValue.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    className="form-control otp-input"
                    value={digit}
                    disabled={isOtpDisabled || isVerifyingOtp}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    maxLength={1}
                    style={otpInputStyle}
                  />
                ))}
              </div>
            </div>

            {/* Security message */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 20,
              padding: "10px 14px",
              backgroundColor: "#f0f7ff",
              borderRadius: 8,
              border: "1px solid #c5e0f5",
            }}>
              <span style={{ color: "#1565c0", fontSize: 16, flexShrink: 0 }}>🔒</span>
              <p style={{ color: "#556", fontSize: 12, margin: 0 }}>
                Your information is encrypted and never shared without consent.
              </p>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="form-section mt-3">
            <h4 className="section-title mb-3" style={headerStyle}>
              Patient Details
            </h4>
            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="patientName"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Name <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="patientName"
                    type="text"
                    className={`form-control ${errors.patientName ? "is-invalid" : ""
                      }`}
                    aria-label="Patient Name"
                    autoComplete="new-password"
                    {...register("patientName")}
                  />
                  {renderErrorMessage(errors.patientName)}
                </div>
              </div>

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="phoneNumberDisplay"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Phone Number <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    type="hidden"
                    {...register("phoneNumberConfirm", {
                      required: "Phone Number is required.",
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Phone Number must be 10 digits.",
                      },
                    })}
                  />
                  <input
                    id="phoneNumberDisplay"
                    type="tel"
                    className="form-control bg-light"
                    value={verifiedPhoneNumber || watchedPhoneNumber || ""}
                    aria-label="Verified Phone Number"
                    readOnly
                    tabIndex={-1}
                    style={{ pointerEvents: "none" }}
                  />
                  {renderErrorMessage(errors.phoneNumberConfirm)}
                </div>
              </div>

              <div className="col-12 col-sm-6">
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: "Gender is required." }}
                  render={({ field }) => (
                    <div className="form-group">
                      <label
                        htmlFor="gender"
                        className="form-label"
                        style={{ display: "flex", alignItems: "start" }}
                      >
                        Gender <span style={{ color: "red" }}> *</span>
                      </label>
                      <select
                        id="gender"
                        className={`form-control ${errors.gender ? "is-invalid" : ""
                          }`}
                        {...field}
                        value={field.value || ""}
                        aria-label="Gender"
                      >
                        <option value="">Select Gender</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="other">Others</option>
                      </select>
                      {renderErrorMessage(errors.gender)}
                    </div>
                  )}
                />
              </div>

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="dateOfBirth"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Date of Birth <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="dateOfBirth"
                    type="date"
                    className={`form-control ${errors.dateOfBirth ? "is-invalid" : ""
                      }`}
                    max={today}
                    aria-label="Date of Birth"
                    {...register("dateOfBirth", {
                      required: "Date of Birth is required.",
                      validate: {
                        notFuture: (value) =>
                          value <= today ||
                          "Date of Birth cannot be in the future.",
                        validDate: (value) => {
                          if (!value) return true;
                          const date = new Date(value);
                          const minDate = new Date("1900-01-01");
                          return (
                            date >= minDate ||
                            "Date of Birth must be after 1900."
                          );
                        },
                      },
                    })}
                  />
                  {renderErrorMessage(errors.dateOfBirth)}
                </div>
              </div>

              <input type="hidden" {...register("country")} />
              <Controller
                name="state"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />
              <Controller
                name="district"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="locality"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Address <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="locality"
                    type="text"
                    className={`form-control ${errors.locality ? "is-invalid" : ""
                      }`}
                    aria-label="Address"
                    {...register("locality", {
                      required: "Address is required.",
                      minLength: {
                        value: 5,
                        message: "Address must be at least 5 characters long.",
                      },
                      maxLength: {
                        value: 200,
                        message: "Address must not exceed 200 characters.",
                      },
                    })}
                  />
                  {renderErrorMessage(errors.locality)}
                </div>
              </div>

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="city"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    City / Village <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    id="city"
                    type="text"
                    className={`form-control ${errors.city ? "is-invalid" : ""
                      }`}
                    aria-label="City or Village"
                    {...register("city", {
                      required: "City / Village is required.",
                      minLength: {
                        value: 2,
                        message:
                          "City / Village must be at least 2 characters long.",
                      },
                      maxLength: {
                        value: 100,
                        message:
                          "City / Village must not exceed 100 characters.",
                      },
                      pattern: {
                        value: /^[a-zA-Z\s'-]+$/,
                        message:
                          "City / Village can only contain letters, spaces, hyphens, and apostrophes.",
                      },
                    })}
                  />
                  {renderErrorMessage(errors.city)}
                </div>
              </div>

              <div className="col-12">
                <div className="form-group">
                  <label
                    htmlFor="pincode"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Pin Code <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="pincode"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    className={`form-control ${errors.pincode ? "is-invalid" : ""
                      }`}
                    aria-label="Pin Code"
                    onInput={(e) => {
                      e.target.value = e.target.value
                        .replace(/[^0-9]/g, "")
                        .slice(0, 6);
                    }}
                    {...register("pincode", {
                      required: "Pin Code is required.",
                      minLength: {
                        value: 6,
                        message: "Pin Code must be exactly 6 digits.",
                      },
                      maxLength: {
                        value: 6,
                        message: "Pin Code must be exactly 6 digits.",
                      },
                      pattern: {
                        value: /^[0-9]{6}$/,
                        message: "Pin Code must be exactly 6 digits.",
                      },
                    })}
                  />
                  {renderErrorMessage(errors.pincode)}
                </div>
              </div>
              {/* //(triaging commented for now) */}
              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="username"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Username <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="username"
                    type="text"
                    className={`form-control ${errors.username ? "is-invalid" : ""
                      }`}
                    aria-label="Username"
                    autoComplete="username"
                    placeholder="Enter username"
                    {...register("username", {
                      required: "Username is required.",
                      minLength: {
                        value: 2,
                        message: "Username must be at least 2 characters long.",
                      },
                      maxLength: {
                        value: 100,
                        message: "Username must not exceed 100 characters.",
                      },
                    })}
                  />
                  {renderErrorMessage(errors.username)}
                </div>
              </div>

              <div className="col-12 col-sm-6">
                <div className="form-group">
                  <label
                    htmlFor="password"
                    className="form-label"
                    style={{ display: "flex", alignItems: "start" }}
                  >
                    Password <span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    id="password"
                    type="password"
                    className={`form-control ${errors.password ? "is-invalid" : ""
                      }`}
                    aria-label="Password"
                    autoComplete="new-password"
                    placeholder="Enter password"
                    {...register("password", {
                      required: "Password is required.",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters long.",
                      },
                      maxLength: {
                        value: 128,
                        message: "Password must not exceed 128 characters.",
                      },
                    })}
                  />
                  {renderErrorMessage(errors.password)}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderPatientLoginPage = () => (
    <div className="patient-login-page">
      <Box className="patient-login-page__bg">
        <Grid
          container
          className="patient-login-page__card"
          sx={{
            width: { xs: "95%", sm: "90%", md: "867px" },
            boxShadow: 3,
            borderRadius: "20px",
            overflow: { xs: "auto", md: "hidden" },
            height: { md: "430px" },
            maxHeight: { xs: "100%", sm: "100%" },
          }}
        >
          <Grid
            item
            xs={12}
            md={4}
            className="patient-login-page__left"
            sx={{
              backgroundColor: "#1d364d",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: { xs: 3, md: 4 },
              minHeight: { xs: "240px", md: "auto" },
            }}
          >
            <h2 style={{ marginBottom: "15px", color: "#fff" }}>Login</h2>
            <h6 style={{ marginBottom: "20px", color: "#fff" }}>
              Your Digital Healthcare Partner
            </h6>
            <Box
              component="img"
              src="/newImages/loginImageGroup.svg"
              alt="Healthcare illustration"
              className="illustration"
              sx={{
                borderRadius: "5px",
                width: { xs: 140, sm: 180, md: 200 },
                height: { xs: 140, sm: 180, md: 200 },
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            className="patient-login-page__right"
            sx={{
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: { xs: 3, md: 4 },
            }}
          >
            <div style={{ width: "100%", maxWidth: "510px" }}>
              <form
                className="contact-list list-unstyled"
                name="patient-login"
                onSubmit={handlePatientLogin}
                autoComplete="off"
                noValidate
              >
                <div style={{ marginBottom: "10px" }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Username"
                    value={loginUsername}
                    onChange={(e) => {
                      setLoginUsername(e.target.value);
                      if (loginError) setLoginError("");
                      if (loginNeedsVerification) setLoginNeedsVerification(false);
                    }}
                    autoComplete="username"
                  />
                </div>
                <div style={{ marginBottom: "10px", position: "relative" }}>
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    className="form-control"
                    placeholder="Password"
                    value={loginPassword}
                    onChange={(e) => {
                      setLoginPassword(e.target.value);
                      if (loginError) setLoginError("");
                      if (loginNeedsVerification) setLoginNeedsVerification(false);
                    }}
                    autoComplete="current-password"
                  />
                  <span
                    className="password-toggle"
                    role="button"
                    tabIndex={0}
                    onMouseDown={() => setShowLoginPassword(true)}
                    onMouseUp={() => setShowLoginPassword(false)}
                    onMouseLeave={() => setShowLoginPassword(false)}
                    onTouchStart={() => setShowLoginPassword(true)}
                    onTouchEnd={() => setShowLoginPassword(false)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                  >
                    {showLoginPassword ? (
                      <i className="fa fa-eye-slash" aria-hidden="true" />
                    ) : (
                      <i className="fa fa-eye" aria-hidden="true" />
                    )}
                  </span>
                </div>
                {loginError && (
                  <div
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "5px",
                      textAlign: "left",
                    }}
                  >
                    {loginError}
                  </div>
                )}
                {loginNeedsVerification && (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                    <button
                      type="button"
                      className="btn btn__secondary"
                      style={{ width: "100%", backgroundColor: "#f57c00", borderColor: "#f57c00" }}
                      onClick={() => {
                        setLoginNeedsVerification(false);
                        setLoginError("");
                        setIsReVerifyingForLogin(true);
                        navigateFlowView("register_phone");
                      }}
                    >
                      Verify Account
                    </button>
                  </div>
                )}
                {!loginNeedsVerification && (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <button
                      type="submit"
                      className="btn btn__secondary mt-10"
                      disabled={isLoginLoading}
                      style={{ width: "100%" }}
                    >
                      {isLoginLoading ? "Logging in..." : "Submit"}
                    </button>
                  </div>
                )}
              </form>
              <div style={{ marginTop: "15px", textAlign: "center" }}>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    toast.info(
                      "Please contact your healthcare facility to reset your password."
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      toast.info(
                        "Please contact your healthcare facility to reset your password."
                      );
                    }
                  }}
                  style={{
                    color: "#0E3F66",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "14px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "#000";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "#0E3F66";
                  }}
                >
                  Forgot Password?
                </span>
              </div>
              <div
                style={{
                  marginTop: "10px",
                  color: "#555",
                  fontSize: "14px",
                  textAlign: "center",
                  maxWidth: "550px",
                  marginLeft: "auto",
                  marginRight: "auto",
                  lineHeight: "1.6",
                }}
              >
                Login to access your secure and personalized health platform.
                Experience the future of healthcare with Hlthtek.com&apos;s
                powerful EHR system aka HTMate!
              </div>
            </div>
          </Grid>
        </Grid>
      </Box>
    </div>
  );

  return (
    <div
      className="wrapper"
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: showLogin
          ? "#f5f8fc"
          : "linear-gradient(135deg, #eaf4fd 0%, #daeef9 50%, #eaf4fd 100%)",
      }}
    >
      <nav
        className="navbar navbar-expand-lg patient-reg-navbar"
        style={{ flex: "0 0 auto", pointerEvents: "none" }}
      >
        <div
          className="container-fluid"
          style={{
            display: "flex",
            justifyContent: isPatientLoggedIn ? "space-between" : "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div className="navbar-brand">
            {navLogoSrc && (
              <img
                src={navLogoSrc}
                className="logo-dark logo-img"
                alt="logo"
              />
            )}
          </div>

          {isPatientLoggedIn && (
            <button
              type="button"
              onClick={handlePatientLogout}
              disabled={isLogoutLoading}
              className="patient-nav-logout"
              style={{ marginRight: isSmallScreen ? 12 : 24, pointerEvents: "auto" }}
            >
              {isLogoutLoading ? (
                <CircularProgress size={18} sx={{ color: "#1d364d" }} />
              ) : (
                "Logout"
              )}
            </button>
          )}
        </div>
      </nav>
      <div
        className={`container my-4 patient-registration-wrapper ${showLogin ? "" : "patient-registration-wrapper--register"
          }`}
        style={{
          flex: "1 1 auto",
          width: "100%",
          position: "relative",
          zIndex: 20,
          pointerEvents: "auto",
        }}
      >
        {showVoiceAssistant ? (
          <>
            <div className="my-appts__header" style={{ maxWidth: 900, margin: "0 auto 20px", padding: "0 16px" }}>
              <div className="my-appts__header-left">
                <button
                  className="my-appts__back-btn"
                  onClick={() => {
                    setShowVoiceAssistant(false);
                    setVoiceShowResults(false);
                    setShowDashboard(true);
                    skipNextAutoPersistRef.current = true;
                    history.goBack();
                  }}
                  aria-label="Go back"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <div className="my-appts__title">
                  {voiceShowResults ? "Assessment Results" : "Book Appointment"}
                  <span>{voiceShowResults ? "Review your results and available doctors" : "Describe your symptoms to find a doctor"}</span>
                </div>
              </div>
            </div>
            <VoiceAssistant
              patient={{ patientId: registeredPatientId, patientUsername: patientUsername, patientPassword: patientPassword }}
              orgCode={orgCode}
              appMode={appMode}
              authToken={htlifeAccessToken}
              onResultsChange={setVoiceShowResults}
              onClose={() => {
                setShowVoiceAssistant(false);
                setVoiceShowResults(false);
                setShowDashboard(true);
                skipNextAutoPersistRef.current = true;
                history.goBack();
              }}
              onGoToMyAppointments={() => {
                setShowVoiceAssistant(false);
                setVoiceShowResults(false);
                setShowMyAppointments(true);
                persistFlowState("appointments", true);
              }}
            />
          </>
        ) : showDashboard ? (
          <div className="patient-dashboard-card">
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <h4 style={{ ...headerStyle, fontSize: isMobile ? "24px" : "30px", marginBottom: 6 }}>
                Welcome
                {patientProfile?.name
                  ? `, ${patientProfile.name}`
                  : patientUsername
                    ? `, ${patientUsername}`
                    : ""}
              </h4>
              <p style={{ color: "#6b7a99", fontSize: 14, margin: 0 }}>
                What would you like to do today?
              </p>
            </div>

            <div className="patient-dashboard-actions">
              <button
                type="button"
                onClick={() => {
                  setShowDashboard(false);
                  setShowVoiceAssistant(true);
                  persistFlowState("voice", true);
                }}
                className="patient-action-btn patient-action-btn--primary"
              >
                Book Appointment
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDashboard(false);
                  setShowMyAppointments(true);
                  persistFlowState("appointments", true);
                }}
                className="patient-action-btn patient-action-btn--outline"
              >
                My Appointments
              </button>
            </div>
          </div>
        ) : showMyAppointments ? (
          <MyAppointments
            htlifeAccessToken={htlifeAccessToken}
            patientUsername={patientUsername}
            patientPassword={patientPassword}
            patientId={registeredPatientId}
            patientName={registeredPatientName}
            orgCode={orgCode}
            appMode={appMode}
            onBack={() => {
              setShowMyAppointments(false);
              setShowDashboard(true);
              skipNextAutoPersistRef.current = true;
              history.goBack();
            }}
            onBookNew={() => {
              setShowMyAppointments(false);
              setShowVoiceAssistant(true);
              persistFlowState("voice", true);
            }}
          />
        ) : showLogin ? (
          renderPatientLoginPage()
        ) : (
          <div className="registration-card mx-auto">
            <div style={{ textAlign: "center", marginBottom: isMobile ? 20 : 28 }}>
              <h4 style={{ ...headerStyle, fontSize: isMobile ? "26px" : "34px", marginBottom: 6 }}>
                Patient Registration
              </h4>
              {activeStep === 0 && (
                <p style={{ color: "#6b7a99", fontSize: 14, margin: 0 }}>
                  Verify your phone to get started. Takes less than a minute.
                </p>
              )}
            </div>

            <Stepper
              activeStep={activeStep}
              orientation="horizontal"
              alternativeLabel
              sx={{ mb: isMobile ? 3 : 4 }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <form onSubmit={handleSubmit(onSubmitPatient)} autoComplete="off">
              {renderStepContent(activeStep)}

              {activeStep === steps.length - 1 && (
                <div
                  className={`pt-3 mt-4 d-flex ${isMobile ? "w-100" : "justify-content-center"
                    }`}
                >
                  <button
                    type="submit"
                    className={`btn btn__secondary mt-6 submit-details-btn ${isMobile ? "w-100" : ""
                      }`}
                    style={isMobile ? {} : { minWidth: "220px" }}
                  >
                    Submit
                  </button>
                </div>
              )}
            </form>
          </div>
        )}
      </div>

      {showSuccessModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(13, 27, 61, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
            padding: 16,
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: 16,
              maxWidth: 420,
              width: "100%",
              padding: "32px 28px",
              textAlign: "center",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "#e6f7ed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <span style={{ color: "#22a559", fontSize: 36, lineHeight: 1 }}>✓</span>
            </div>
            <h4 style={{ color: "#0d1b3d", fontWeight: 700, marginBottom: 8 }}>
              Successfully Registered
            </h4>
            <p style={{ color: "#41506b", margin: "0 0 6px" }}>Your Patient ID is</p>
            <p
              style={{
                color: "#0E3F66",
                fontWeight: 700,
                fontSize: 20,
                margin: "0 0 16px",
                wordBreak: "break-all",
              }}
            >
              {registeredPatientId}
            </p>
            <p style={{ color: "#6b7a99", fontSize: 14, margin: 0 }}>
              You will be redirected to the login screen in{" "}
              <strong>{successCountdown}</strong> second
              {successCountdown === 1 ? "" : "s"}…
            </p>
            <button
              onClick={() => {
                setShowSuccessModal(false);
                setShowLogin(true);
                persistFlowState("login");
              }}
              className="btn btn__secondary mt-4"
              style={{ minWidth: 180 }}
            >
              Go to Login now
            </button>
          </div>
        </div>
      )}

      <div className="reg-footer">
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: "#F4F4F4" }}
        >
          Powered by
          <img
            src={NavLogoFooter}
            alt="logo"
            style={{
              marginLeft: "6px",
              verticalAlign: "middle",
              height: isSmallScreen ? "20px" : "24px",
              width: "auto",
            }}
          />
        </Typography>
      </div>
    </div>
  );
}

export default PatientRegistrationWizard;
